import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { PlusCircle, Trash2, Bed, Loader2 } from 'lucide-react';
import { filterCamas } from '@/services/camas.service';

const CamasSelector = ({ value = [], onChange, disabled = false }) => {
  const [camasDisponibles, setCamasDisponibles] = useState([]);
  const [loadingCamas, setLoadingCamas] = useState(false);
  const [selectedCamaId, setSelectedCamaId] = useState('');
  const [cantidad, setCantidad] = useState(1);

  useEffect(() => {
    loadCamas();
  }, []);

  const loadCamas = async () => {
    setLoadingCamas(true);
    try {
      const response = await filterCamas({ estado: 'Activo' }, 1000, 1);
      setCamasDisponibles(response.data || []);
    } catch (error) {
      console.error('Error al cargar camas:', error);
    } finally {
      setLoadingCamas(false);
    }
  };

  const handleAddCama = () => {
    if (!selectedCamaId || cantidad < 1) return;

    const camaSeleccionada = camasDisponibles.find(c => c._id === selectedCamaId);
    if (!camaSeleccionada) return;

    // Verificar si ya existe
    const yaExiste = value.some(c => c.camaId === selectedCamaId);
    if (yaExiste) {
      return;
    }

    const nuevaCama = {
      camaId: camaSeleccionada._id,
      nombre: camaSeleccionada.nombre,
      cantidad: parseInt(cantidad)
    };

    onChange([...value, nuevaCama]);
    setSelectedCamaId('');
    setCantidad(1);
  };

  const handleRemoveCama = (camaId) => {
    onChange(value.filter(c => c.camaId !== camaId));
  };

  const handleUpdateCantidad = (camaId, nuevaCantidad) => {
    onChange(value.map(c => 
      c.camaId === camaId 
        ? { ...c, cantidad: parseInt(nuevaCantidad) || 1 }
        : c
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Bed className="h-5 w-5 text-blue-600" />
          Tipos de Camas
        </Label>
        <span className="text-sm text-gray-500">
          {value.length} tipo(s) de cama
        </span>
      </div>

      {/* Formulario para agregar cama */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          {loadingCamas ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-600">Cargando camas...</span>
            </div>
          ) : camasDisponibles.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-500">
              No hay tipos de camas disponibles.
              <br />
              Por favor, crea tipos de camas primero en Parametrización → Camas.
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-7 space-y-2">
                <Label htmlFor="cama-select" className="text-sm">Tipo de Cama</Label>
                <Select 
                  value={selectedCamaId} 
                  onValueChange={setSelectedCamaId}
                  disabled={disabled}
                >
                  <SelectTrigger id="cama-select">
                    <SelectValue placeholder="Selecciona un tipo de cama..." />
                  </SelectTrigger>
                  <SelectContent>
                    {camasDisponibles.map((cama) => (
                      <SelectItem key={cama._id} value={cama._id}>
                        {cama.nombre} (Capacidad: {cama.capacidad} {cama.capacidad === 1 ? 'persona' : 'personas'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-4 space-y-2">
                <Label htmlFor="cantidad-cama" className="text-sm">Cantidad</Label>
                <Input
                  id="cantidad-cama"
                  type="number"
                  min="1"
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  placeholder="1"
                  disabled={disabled}
                />
              </div>

              <div className="col-span-1">
                <Button
                  type="button"
                  onClick={handleAddCama}
                  size="sm"
                  className="w-full"
                  disabled={!selectedCamaId || cantidad < 1 || disabled}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de camas agregadas */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((cama, index) => (
            <Card key={cama.camaId} className="border-l-4 border-l-blue-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <Bed className="h-5 w-5 text-blue-600" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{cama.nombre}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-600">Cantidad:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={cama.cantidad}
                        onChange={(e) => handleUpdateCantidad(cama.camaId, e.target.value)}
                        className="w-20 h-8 text-sm"
                        disabled={disabled}
                      />
                    </div>
                    
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCama(cama.camaId)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {value.length === 0 && !loadingCamas && (
        <div className="text-center py-6 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
          No hay tipos de camas agregados
        </div>
      )}
    </div>
  );
};

export default CamasSelector;
